// Singleton SignalR connection manager — ONE WebSocket per logged-in user, shared by every
// real-time feature. Features never build their own connection; they subscribe by resource type
// (see useRealtimeResource). Auth: the WebSocket handshake can't carry an Authorization header,
// and the JWT (~7 KB) is too large for a query string behind IIS (rejected with 404.15), so
// accessTokenFactory first swaps the JWT for a short single-use ticket (BackOffice/Realtime/ticket)
// and sends that as ?access_token=… — the server's JWT OnMessageReceived hook redeems it back to
// the JWT. A fresh ticket is fetched on every (re)connect, so token refreshes are picked up too.
import {
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

const HUB_PATH = "hubs/realtime";
const SERVER_EVENT = "ReceiveRealtimeEvent"; // must match RealtimeBroadcaster.ClientMethod

// Master kill-switch for the whole real-time layer. While Batch Receive (the only consumer) is
// hidden from the UI, we keep the SignalR WebSocket and its connection-ticket polling
// (BackOffice/Realtime/ticket) fully off so neither runs in production. Disabled unless
// REACT_APP_REALTIME_ENABLED is explicitly "true" — flip that env var and rebuild to re-enable.
const REALTIME_ENABLED =
  String(process.env.REACT_APP_REALTIME_ENABLED).toLowerCase() === "true";

function buildHubUrl() {
  // REACT_APP_HUB_URL lets ops point SignalR straight at the API (bypassing the gateway) if needed.
  const explicit = process.env.REACT_APP_HUB_URL;
  if (explicit) return `${explicit.replace(/\/+$/, "")}/${HUB_PATH}`;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  return `${base}/${HUB_PATH}`;
}

let connection = null;
let startPromise = null;
let retryTimer = null;

const handlers = new Map(); // resourceType -> Set<handler(envelope)>
const stateListeners = new Set(); // fn("connected" | "reconnecting" | "disconnected")
const joined = new Map(); // key -> { resourceType, resourceId } (re-joined after reconnect)

function emitState(state) {
  for (const fn of stateListeners) {
    try {
      fn(state);
    } catch {
      /* ignore listener error */
    }
  }
}

// Trade the JWT (sent in the normal Authorization header by the axios interceptor) for a
// short single-use connection ticket. Falls back to the raw JWT if the ticket endpoint is
// unavailable — that still works on Kestrel/dev, which has no query-string limit.
async function fetchConnectionTicket() {
  try {
    const res = await axios.post(API_URL.REALTIME.TICKET);
    const ticket = res?.data?.data;
    if (ticket) return ticket;
  } catch {
    /* fall through to raw token */
  }
  return localStorage.getItem("token") || "";
}

function buildConnection() {
  const conn = new HubConnectionBuilder()
    .withUrl(buildHubUrl(), {
      accessTokenFactory: fetchConnectionTicket,
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();

  conn.on(SERVER_EVENT, (envelope) => {
    if (!envelope || !envelope.resourceType) return;
    const set = handlers.get(envelope.resourceType);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(envelope);
      } catch {
        /* one bad handler shouldn't break the rest */
      }
    }
  });

  conn.onreconnecting(() => emitState("reconnecting"));
  conn.onreconnected(async () => {
    // Group membership is per-connection — re-join everything we were watching.
    for (const { resourceType, resourceId } of joined.values()) {
      try {
        await conn.invoke("JoinResource", resourceType, resourceId);
      } catch {
        /* ignore */
      }
    }
    emitState("connected");
  });
  conn.onclose(() => {
    emitState("disconnected");
    scheduleRetry();
  });
  return conn;
}

// After automatic reconnect gives up, keep trying gently so real-time self-heals without a
// page reload. (The feature's polling fallback covers the gap meanwhile.)
function scheduleRetry() {
  if (retryTimer || !localStorage.getItem("token")) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    ensureStarted().catch(() => {});
  }, 15000);
}

export async function ensureStarted() {
  if (!REALTIME_ENABLED) return null; // real-time layer disabled — no socket, no ticket polling
  if (!localStorage.getItem("token")) return null; // not logged in
  if (!connection) connection = buildConnection();
  if (connection.state === HubConnectionState.Connected) return connection;
  if (
    connection.state === HubConnectionState.Connecting ||
    connection.state === HubConnectionState.Reconnecting
  ) {
    return startPromise ? startPromise.catch(() => null) : connection;
  }

  startPromise = connection
    .start()
    .then(async () => {
      emitState("connected");
      // (Re)join anything requested before the socket was ready.
      for (const { resourceType, resourceId } of joined.values()) {
        try {
          await connection.invoke("JoinResource", resourceType, resourceId);
        } catch {
          /* ignore */
        }
      }
      return connection;
    })
    .catch((err) => {
      emitState("disconnected");
      scheduleRetry();
      throw err;
    })
    .finally(() => {
      startPromise = null;
    });

  return startPromise;
}

export async function stop() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  joined.clear();
  handlers.clear();
  const c = connection;
  connection = null;
  startPromise = null;
  if (c) {
    try {
      await c.stop();
    } catch {
      /* ignore */
    }
  }
}

export function isConnected() {
  return !!connection && connection.state === HubConnectionState.Connected;
}

export function onConnectionState(fn) {
  stateListeners.add(fn);
  try {
    fn(isConnected() ? "connected" : "disconnected"); // push current state immediately
  } catch {
    /* ignore */
  }
  return () => stateListeners.delete(fn);
}

export function onResource(resourceType, handler) {
  if (!handlers.has(resourceType)) handlers.set(resourceType, new Set());
  handlers.get(resourceType).add(handler);
  return () => {
    const set = handlers.get(resourceType);
    if (set) {
      set.delete(handler);
      if (set.size === 0) handlers.delete(resourceType);
    }
  };
}

export async function joinResource(resourceType, resourceId) {
  joined.set(`${resourceType}:${resourceId}`, { resourceType, resourceId });
  let conn = null;
  try {
    conn = await ensureStarted();
  } catch {
    conn = null;
  }
  if (conn && conn.state === HubConnectionState.Connected) {
    try {
      await conn.invoke("JoinResource", resourceType, resourceId);
    } catch {
      /* ignore — onreconnected/ensureStarted will re-join */
    }
  }
}

export async function leaveResource(resourceType, resourceId) {
  joined.delete(`${resourceType}:${resourceId}`);
  if (connection && connection.state === HubConnectionState.Connected) {
    try {
      await connection.invoke("LeaveResource", resourceType, resourceId);
    } catch {
      /* ignore */
    }
  }
}
